import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * (Multi-gift: gift 1 = spoon via trigger/subtotal, gift 2 = subtotal-only via data-gift2-*.)
 * Keeps a merchant-configured free gift line in sync with cart eligibility
 * and, when a trigger product is configured, scales the gift's own quantity
 * with the trigger product's quantity (e.g. 2 free spoons per 1 Diabet Cure,
 * 4 for 2, etc).
 *
 * Reads its configuration from data attributes on <cart-items-component>
 * (written by snippets/cart-items-component.liquid, itself fed by
 * snippets/cart-drawer.liquid and sections/main-cart.liquid from the
 * cart-drawer-section's theme-editor settings), so changing the gift
 * product, trigger product, minimum subtotal, or quantity-per-trigger-unit
 * in the theme editor takes effect with no code changes. <cart-items-component>
 * is used for both the cart drawer and the cart page, so this works
 * identically on both.
 *
 * Two eligibility modes, chosen per the configured settings:
 *  - Trigger product mode (data-gift-trigger-variant-id set): the gift's
 *    target quantity is (trigger product's quantity in cart) *
 *    (data-gift-quantity-per-trigger-unit, default 2). Zero when the trigger
 *    product isn't in the cart.
 *  - Subtotal mode (no trigger product configured): the gift's target
 *    quantity is 1 once the cart subtotal (excluding the gift line) reaches
 *    the configured minimum, otherwise 0.
 *
 * Rides the theme's existing cart-update event bus (the same
 * StandardEvents.cartLinesUpdate / CartLinesUpdateEvent contract every other
 * cart component and third-party app already uses) instead of polling or
 * owning its own render path — cart-items-component and cart-icon pick up
 * the resulting section HTML themselves.
 *
 * SPEED: after a quantity change made inside the cart, the theme's own response already contains the
 * full, current cart (`detail.items`), so we reconcile against that directly and skip the extra
 * /cart.js request. For every other source (add to cart, add-ons, discounts ...) the shape of the
 * event result varies, so there we still fetch the authoritative cart from /cart.js.
 */
const GIFT_SOURCE = 'cart-gift-manager';

/** Re-entrancy guard: one add/remove/update mutation in flight at a time. */
let mutationInFlight = false;

/**
 * Reads every configured gift. Gift 1 (spoon) may use trigger-product mode;
 * gift 2 (data-gift2-*) is always subtotal-only, quantity 1.
 * @returns {Array<{variantId: number, triggerVariantId: number | null, quantityPerTriggerUnit: number, minimumSubtotalCents: number}>}
 */
function readGiftConfigs() {
  const host = document.querySelector('cart-items-component[data-gift-variant-id], cart-items-component[data-gift2-variant-id]');
  if (!host) return [];
  const configs = [];

  const variantId = Number(host.getAttribute('data-gift-variant-id'));
  if (Number.isFinite(variantId) && variantId > 0) {
    const rawTrigger = host.getAttribute('data-gift-trigger-variant-id');
    const triggerVariantId =
      rawTrigger && Number.isFinite(Number(rawTrigger)) && Number(rawTrigger) > 0 ? Number(rawTrigger) : null;
    const parsedQty = Number(host.getAttribute('data-gift-quantity-per-trigger-unit'));
    const quantityPerTriggerUnit = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 2;
    const minimumSubtotalCents = Number(host.getAttribute('data-gift-minimum-subtotal-cents'));
    if (triggerVariantId || (Number.isFinite(minimumSubtotalCents) && minimumSubtotalCents >= 0)) {
      configs.push({ variantId, triggerVariantId, quantityPerTriggerUnit, minimumSubtotalCents });
    }
  }

  const variant2 = Number(host.getAttribute('data-gift2-variant-id'));
  const min2 = Number(host.getAttribute('data-gift2-minimum-subtotal-cents'));
  if (Number.isFinite(variant2) && variant2 > 0 && Number.isFinite(min2) && min2 >= 0) {
    configs.push({ variantId: variant2, triggerVariantId: null, quantityPerTriggerUnit: 1, minimumSubtotalCents: min2 });
  }
  return configs;
}

/**
 * @returns {string} Comma-separated list of every rendered cart-items-component's section id.
 */
function collectSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    const id = element.getAttribute('data-section-id');
    if (id) ids.add(id);
  });
  return Array.from(ids).join(',');
}

/**
 * @param {{variantId: number}} config
 * @param {{items: Array<{variant_id: number, quantity: number}>}} cart
 */
function findGiftLine(config, cart) {
  return cart.items?.find((item) => item.variant_id === config.variantId) ?? null;
}

/** @returns {number} Total quantity of the configured trigger product in the cart. */
function triggerQuantityInCart(config, cart) {
  return (cart.items ?? [])
    .filter((item) => item.variant_id === config.triggerVariantId)
    .reduce((sum, item) => sum + (item.quantity || 0), 0);
}

/** @returns {number} Cart subtotal in cents, excluding every gift line. */
function subtotalExcludingGifts(configs, cart) {
  const giftIds = new Set(configs.map((c) => c.variantId));
  return (cart.items ?? [])
    .filter((item) => !giftIds.has(item.variant_id))
    .reduce((sum, item) => sum + item.final_line_price, 0);
}

/**
 * Sets one or more gift lines to exact quantities in ONE /cart/update.js call
 * (it sets, not adds) and broadcasts the result on the shared cart event.
 * @param {Record<number, number>} updates - variantId -> target quantity (0 removes)
 */
async function setGiftQuantities(updates) {
  const sections = collectSectionIds();
  const body = JSON.stringify({ updates, sections, sections_url: window.location.pathname });
  const lines = Object.entries(updates).map(([id, quantity]) => ({ id: String(id), quantity }));
  const anyAdd = lines.some((l) => l.quantity > 0);

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: anyAdd ? 'add' : 'remove',
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const parsed = await response.json();
    if (parsed.errors) {
      throw new Error(typeof parsed.errors === 'string' ? parsed.errors : JSON.stringify(parsed.errors));
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed),
      detail: {
        sections: parsed.sections,
        items: parsed.items,
        itemCount: parsed.item_count,
        source: GIFT_SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error(String(error)));
    console.warn('[cart-gift-manager] Failed to sync free gift:', error);
  }
}

/**
 * Compares the live cart against each gift's rule and resizes gift lines to
 * match, in a single request. No-ops while a mutation is in flight.
 */
async function reconcile(cart) {
  if (mutationInFlight) return;
  if (!cart || !Array.isArray(cart.items)) return;

  const configs = readGiftConfigs();
  if (!configs.length) return;

  const subtotal = subtotalExcludingGifts(configs, cart);
  const updates = {};
  for (const config of configs) {
    let target;
    if (config.triggerVariantId) {
      const t = triggerQuantityInCart(config, cart);
      target = t > 0 ? t * config.quantityPerTriggerUnit : 0;
    } else {
      target = subtotal >= config.minimumSubtotalCents ? 1 : 0;
    }
    const line = findGiftLine(config, cart);
    const current = line ? line.quantity : 0;
    if (current !== target) updates[config.variantId] = target;
  }
  if (!Object.keys(updates).length) return;

  mutationInFlight = true;
  try {
    await setGiftQuantities(updates);
  } finally {
    mutationInFlight = false;
  }
}

/**
 * Fetches the authoritative cart from the Ajax API and reconciles against
 * it. Used instead of trusting the shape of whatever cart object (if any)
 * rides along on a cart-lines-update event, since that shape varies by
 * dispatcher and isn't guaranteed to include `.items`.
 */
async function reconcileFromServer() {
  if (!readGiftConfigs().length) return;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    await reconcile(await response.json());
  } catch (error) {
    console.warn('[cart-gift-manager] Reconcile failed:', error);
  }
}

/** @param {CartLinesUpdateEvent} event */
function onCartLinesUpdate(event) {
  event.promise
    ?.then((result) => {
      const detail = result?.detail;
      // Skip reconciling against our own mutation's result — it already
      // reflects the target state, and re-checking now would fight with
      // the in-flight guard used by the mutation that produced it.
      if (detail?.source === GIFT_SOURCE) return;
      // SPEED: the cart page / drawer's own quantity change returns the full cart (Ajax change.js),
      // so no extra /cart.js request is needed.
      if (detail?.source === 'cart-items-component' && Array.isArray(detail.items)) {
        reconcile({ items: detail.items });
        return;
      }
      reconcileFromServer();
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-gift-manager] Event promise rejected:', error);
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
reconcileFromServer();
